import React, { useRef } from 'react';
import { ReactComponent as Search } from 'assets/img/search.svg';
import { ReactComponent as Close } from 'assets/img/close.svg';

export default function SearchBox({
  searchQuery,
  searchResults,
  setSearchQuery,
  isSearchResultsLoading,
  updateSelected,
  selected,
  clearButtonClasses = '',
}) {
  const inputRef = useRef();

  let handleSearchResultClick = function (record) {
    if (record.type === 'state') {
      setSearchQuery(`${record.statename}`);
      updateSelected(record);
    } else if (record.type === 'county') {
      setSearchQuery(`${record.countyname}`);
      updateSelected(record);
    } else if (record.type === 'tract') {
      setSearchQuery(`${record.tractcode}`);
      updateSelected(record);
    }
  };

  let handleInputChange = function (value) {
    updateSelected(null);
    setSearchQuery(value);
  };

  let handleInputClear = function () {
    updateSelected(null);
    setSearchQuery('');
    inputRef.current.focus();
  };

  return (
    <div data-component-name='SearchBox' className='flex flex-col group bg-white-400'>
      <div className='h-[60px] relative'>
        <input
          ref={inputRef}
          type='text'
          value={searchQuery}
          placeholder='Search by state, county or tract code'
          onChange={(e) => handleInputChange(e.target.value)}
          className={`w-full h-full px-4 text-base text-blue-500 border-b outline-none border-white-400 placeholder:opacity-100 placeholder:text-blue-300 placeholder:text-md placeholder:font-normal ${
            isSearchResultsLoading ||
            (searchResults && searchResults.length > 0 && !selected) ||
            (searchResults &&
              searchResults.length === 0 &&
              !selected &&
              searchQuery?.length > 0)
              ? 'group-focus-within:border-blue-100'
              : ''
          }`}
        />

        {/* Selected tract value overlay. */}
        {selected && (
          <>
            {selected.type === 'state' && (
              <div className='absolute top-0 flex items-center w-full h-full gap-3 px-4 text-base text-blue-500 -translate-y-px pointer-events-none'>
                <span className='truncate opacity-0 whitespace-nowrap pr-11'>
                  {selected.statename}
                </span>
              </div>
            )}
            {selected.type === 'county' && (
              <div className='absolute top-0 flex items-center w-full h-full px-4 text-base text-blue-500 -translate-y-px pointer-events-none'>
                <span className='opacity-0'>{selected.countyname}</span>
                <span className='text-blue-300 truncate whitespace-nowrap pr-11'>
                  , {selected.statename}
                </span>
              </div>
            )}
            {selected.type === 'tract' && (
              <div className='absolute top-0 flex items-center w-full h-full gap-3 px-4 text-base text-blue-500 -translate-y-px pointer-events-none'>
                <span className='opacity-0'>{selected.tractcode}</span>
                <span className='text-blue-300 truncate whitespace-nowrap pr-11'>
                  {selected.countyname}, {selected.statename}
                </span>
              </div>
            )}
          </>
        )}

        {/* Search icon overlay. */}
        <div className='absolute top-0 right-0 flex items-center justify-center w-12 h-full pointer-events-none'>
          <div
            className={`transition-opacity duration-100 ${
              selected ? 'opacity-0' : 'opacity-100'
            }`}
          >
            <Search />
          </div>
          <button
            type='button'
            onClick={() => handleInputClear()}
            className={`transition-opacity duration-100 absolute top-0 left-0 w-full h-full flex justify-center items-center ${
              selected
                ? clearButtonClasses
                  ? `${clearButtonClasses} opacity-100`
                  : 'opacity-100 pointer-events-auto'
                : 'opacity-0'
            }`}
          >
            <Close className='text-gray-200' />
          </button>
        </div>
      </div>

      {searchResults && searchResults.length > 0 && !selected && (
        <ul className='flex-col hidden py-2 group-focus-within:flex max-h-[calc(100vh_-_275px)] overflow-y-scroll'>
          {searchResults.map((record, index) => (
            <div key={index}>
              {record.type === 'state' && (
                <li className='flex-shrink-0 h-10' key={index}>
                  <button
                    type='button'
                    onClick={() => handleSearchResultClick(record)}
                    className='flex items-center w-full h-full px-4 text-base text-blue-500 hover:bg-blue-50'
                  >
                    {record.statename}
                  </button>
                </li>
              )}
              {record.type === 'county' && (
                <li className='flex-shrink-0 h-10' key={index}>
                  <button
                    type='button'
                    onClick={() => handleSearchResultClick(record)}
                    className='flex items-center w-full h-full px-4 text-base text-blue-500 hover:bg-blue-50'
                  >
                    {record.countyname}
                    <span className='text-blue-300'>, {record.statename}</span>
                  </button>
                </li>
              )}
              {record.type === 'tract' && (
                <li className='flex-shrink-0 h-10' key={index}>
                  <button
                    type='button'
                    onClick={() => handleSearchResultClick(record)}
                    className='flex items-center w-full h-full px-4 text-base text-blue-500 hover:bg-blue-50 gap-7'
                  >
                    {record.tractcode}
                    <span className='text-blue-300 truncate whitespace-nowrap'>
                      {record.countyname}, {record.statename}
                    </span>
                  </button>
                </li>
              )}
            </div>
          ))}
        </ul>
      )}
      {isSearchResultsLoading && !selected && searchQuery?.length > 0 && (
        <div className='items-center hidden h-10 px-4 my-2 text-base text-blue-300 group-focus-within:flex'>
          Loading...
        </div>
      )}
      {searchResults &&
        searchResults.length === 0 &&
        !selected &&
        searchQuery?.length > 0 && (
          <div className='items-center hidden h-10 px-4 my-2 text-base text-blue-300 group-focus-within:flex'>
            No results
          </div>
        )}
    </div>
  );
}
